/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types'
], function(
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes
) {
/**
 * @class DropDown
 * @description Creates a property control of type Dropdown.
 * Options represents parameters supported by this control
 * @param {string} options.id property id
 * @param {boolean} options.disabled default is false, set to true if you want the control disabled
 * @param {string} options.defaultValue the value of the option that should be selected by default
 * @param {string} options.style css styles to apply to the select control
 * @param {object[]} options.options Array of objects for each option in the select control
 * @param {string} options.options.label Label for the option in the select control
 * @param {string} options.options.value Value for the option in the select control
 * @param {boolean} options.options.selected true if the option should be selected by default
 * @param {function} options.onChange callback to be notified as soon as the change happens
 * @param {string} [options.ariaDescribedby] dropdown title and also use this option to provide more information for screen readers
 * @example of DropDown
 * {
 * 	'id': 'fontSize'
 * 	'type': 'DropDown',
 * 	label': 'Font size',
 * 	ariaDescribedby': 'Font size',
 * 	'options': [
 * 		{
 * 			'value': '8',
 * 			'label': '8',
 * 			'default': true
 * 		},
 * 		{
 * 			'value': '9',
 * 			'label': '9'
 * 		}
 * 	],
 * 	'style': 'width:88px;'
 * }
 *
 */

	class DropDown extends React.Component {
		render() {
			const { item, style } = this.props;
			const { id, setPropertyValue, getPropertyValue } = item;
			if (!id) {
				return null;
			}
			let layoutSpec = item;
			if (item && item.editor && item.editor.uiControl) {
				layoutSpec = item.editor.uiControl;
			}
			let { options, getOptions, ariaDescribedby, readOnly, label } = layoutSpec;
			if (!options || !options.length) {
				if (getOptions && typeof getOptions === 'function') {
					options = getOptions();
				} else {
					return null;
				}
			}
			let defaultSelectedItem;
			const defaultSelectedValue = getPropertyValue();
			if(defaultSelectedValue !== undefined){
				defaultSelectedItem = options.find(option => option.value === defaultSelectedValue);
			}
			if(!defaultSelectedItem){
				defaultSelectedItem = options.find(option => option.selected === true) || options[0];
			}
			const dropDownLabel = label ? label : ariaDescribedby;
			return (
				<div style={style} className='properties--controls--dropDown'>
					<BA_UI_CARBON.Dropdown
						id={id}
						titleText={dropDownLabel}
						items={options}
						size='sm'
						initialSelectedItem={ defaultSelectedItem }
						disabled={readOnly}
						onChange={(event) => {
							setPropertyValue(event.selectedItem.value);
						}}
					/>
				</div>
			);
		}
	}

	DropDown.propTypes = {
		item: PropTypes.shape({
			id: PropTypes.string.isRequired,
			type: 'DropDown',
			ariaDescribedby: PropTypes.string,
			label: PropTypes.string,
			options: PropTypes.array.isRequired,
			style: PropTypes.string,
			disabled: PropTypes.bool,
			setPropertyValue: PropTypes.func.isRequired,
			getPropertyValue: PropTypes.func.isRequired,
			editor: PropTypes.object
		}),
		style: PropTypes.object
	};
	DropDown.defaultProps = {};

	return MobxReact.observer(DropDown);
});
